<div class="w-full py-2">
  
  <div *ngIf="isLoading" class="flex justify-center items-center py-20">
    <div class="w-4 h-4 rounded-full animate-pulse bg-[#1D4586] mr-1"></div>
    <div class="w-4 h-4 rounded-full animate-pulse bg-[#1D4586] mr-1"></div>
    <div class="w-4 h-4 rounded-full animate-pulse bg-[#1D4586]"></div>
  </div>

  <ng-container *ngIf="!isLoading">
    <!-- Header Area -->
    <div class="flex justify-between items-end mb-6">
      <div>
        <h2 class="text-xl font-bold text-gray-900">Your assessments</h2>
        <p class="text-sm text-gray-500">Pass mark 75% &middot; one retake if you fall short</p>
      </div>
      
      <!-- Progress Indicator -->
      <div class="flex flex-col items-end w-48">
        <span class="text-xs text-gray-500 mb-1 font-medium">{{ completedCount }} of {{ totalAssessments }} completed</span>
        <div class="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden flex">
          <div class="bg-[#1D4586] h-full transition-all" [style.width.%]="(completedCount / totalAssessments) * 100"></div>
        </div>
      </div>
    </div>

    <!-- Assessment List -->
    <div class="space-y-3 mb-8">
      <div *ngFor="let item of assessments" class="bg-white border border-gray-200 rounded-lg p-5 flex items-center justify-between shadow-sm">
        
        <!-- Left details -->
        <div>
          <h3 class="font-bold text-gray-900 text-[15px]">{{ item.title }}</h3>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ item.question_count }} questions
            <span *ngIf="item.status === 'not_cleared' && item.can_retake">&middot; retake uses a different question set</span>
            <span *ngIf="item.status === 'not_cleared' && !item.can_retake">&middot; no further attempts</span>
          </p>
        </div>

        <!-- Right actions -->
        <div class="flex items-center space-x-4">
          <span class="px-3 py-1 rounded-full text-xs font-semibold" [ngClass]="getBadgeClass(item)">
            {{ getBadgeText(item) }}
          </span>

          <button (click)="onAction(item)" 
                  [ngClass]="getButtonText(item) === 'View results' ? 'bg-white text-[#1D4586] border border-[#1D4586] hover:bg-gray-50' : 'bg-[#1D4586] text-white hover:bg-[#16366b]'"
                  class="px-5 py-2 rounded font-semibold text-sm w-32 transition-colors">
            {{ getButtonText(item) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Interactive Feedback Banner -->
    <div class="bg-[#E9F0E6] rounded-lg p-5 flex items-center justify-between border border-[#cbe0c5]">
      <div>
        <h3 class="font-bold text-gray-900 text-[15px]">Feedback on the induction training</h3>
        <p class="text-xs text-gray-500 mt-0.5">Rate it and add a comment if you like &mdash; optional.</p>
      </div>
      <div class="flex items-center space-x-4">
        
        <!-- Interactive Stars -->
        <div class="flex space-x-1 cursor-pointer">
          <svg *ngFor="let star of [1, 2, 3, 4, 5]" 
               (click)="setRating(star)"
               [ngClass]="star <= overallRating ? 'text-[#1D4586] fill-current' : 'text-gray-300 fill-current'"
               class="w-5 h-5 transition-colors" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </div>

        <!-- Add/Edit Comment Button -->
        <button (click)="openCommentModal()" 
                [disabled]="overallRating === 0"
                [ngClass]="overallRating > 0 ? 'bg-[#D9EAD5] border-[#cbe0c5] hover:bg-[#cce2c7] text-[#1A5C3D]' : 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'"
                class="border px-4 py-1.5 rounded font-semibold text-xs transition-colors">
          {{ overallComment ? 'Edit comment' : 'Add a comment' }}
        </button>
      </div>
    </div>
  </ng-container>

  <!-- Comment Modal -->
  <div *ngIf="showCommentModal" class="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[100] px-4 backdrop-blur-sm">
    <div class="bg-white p-6 rounded-lg shadow-2xl w-full max-w-lg">
      <h3 class="text-lg font-bold text-gray-900 mb-2">Additional Feedback</h3>
      <textarea [(ngModel)]="overallComment" 
                rows="4" 
                placeholder="Tell us what you thought..."
                class="w-full border border-gray-300 rounded p-3 text-sm focus:ring-1 focus:ring-[#1D4586] focus:border-[#1D4586] outline-none resize-none mb-4"></textarea>
      
      <div class="flex justify-end space-x-3">
        <button (click)="showCommentModal = false" class="px-4 py-2 rounded text-gray-600 hover:bg-gray-100 text-sm font-semibold transition-colors">Cancel</button>
        <button (click)="submitFeedback()" 
                [disabled]="isSubmittingFeedback"
                class="bg-[#1D4586] text-white px-6 py-2 rounded text-sm font-semibold hover:bg-[#16366b] disabled:opacity-50 transition-colors flex items-center">
          <span *ngIf="isSubmittingFeedback" class="mr-2 h-4 w-4 rounded-full border-2 border-t-transparent border-white animate-spin"></span>
          Save
        </button>
      </div>
    </div>
  </div>

</div>